import {PIXI} from '@colonist/pixi'
import {TextTruncatedView} from '../../ui_canvas_engine/TextTruncatedView'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {PlayerValidator, MechanicValidatorDevelopmentCardPlayer, MechanicValidatorProgressCardsPlayer} from '@colonist/game-mechanic-validators'
import {UIGameManager} from '../UIGameManager'
import {UIPlayerOptionsPopup} from '../../ui_player_options_popup/UIPlayerOptionsPopup'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {VictoryPointStateReader} from '@colonist/game-base-types'
import {UIGameMechanic} from '../UIGameEventController'
import {CardView} from '../../ui_canvas_engine/CardView'
import {RibbonCountView} from '../../ui_canvas_engine/RibbonCountView'
import {AdaptiveSpriteSectionView} from '../../ui_canvas_engine/AdaptiveSpriteSectionView'
import {UIGamePlayerAvatarView} from './UIGamePlayerAvatarView'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {TranslationObject} from '@colonist/i18n'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {Sprites} from '../UIGameImageDictionary'
import {
    MechanicCityImprovementsPlayerState,
    MechanicCityPlayerState,
    MechanicCityWallPlayerState,
    MechanicLargestArmyPlayerState,
    MechanicLongestRoadPlayerState,
    MechanicRoadPlayerState,
    MechanicSettlementPlayerState,
    MechanicSpecialBuildPhaseState,
    PlayerColor,
    PlayerState,
    TurnState,
} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {uiManagerEvents} from '../../EntryShared.ts'

export enum UIPlayerAdditionalInformationLayer {
    Layer0,
    Layer1,
    Layer2,
    Layer3,
}

export abstract class UIGamePlayerInformationView extends SectionBgView implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager

    player: PlayerValidator
    playerState: PlayerState
    playerName: TextTruncatedView
    playerAvatar: UIGamePlayerAvatarView
    victoryPointRibbon: RibbonCountView

    resourceCardView: CardView
    developmentCardView: CardView

    specialIconView: AdaptiveSpriteSectionView

    developmentCardsPlayerState: MechanicValidatorDevelopmentCardPlayer | undefined
    progressCardsPlayerValidator: MechanicValidatorProgressCardsPlayer | undefined
    settlementPlayerState: MechanicSettlementPlayerState | undefined
    cityPlayerState: MechanicCityPlayerState | undefined
    cityWallPlayerState: MechanicCityWallPlayerState | undefined
    cityImprovementPlayerState: MechanicCityImprovementsPlayerState | undefined
    roadPlayerState: MechanicRoadPlayerState | undefined
    longestRoadPlayerState: MechanicLongestRoadPlayerState | undefined
    largestArmyPlayerState: MechanicLargestArmyPlayerState | undefined
    specialBuildPhaseState: MechanicSpecialBuildPhaseState | undefined

    constructor(uiGameManager: UIGameManager, player: PlayerValidator, playerState: PlayerState, x: number, y: number, w: number, h: number, container: PIXI.Container, myPlayer: boolean,
                developmentCardsPlayerState: MechanicValidatorDevelopmentCardPlayer | undefined,
                progressCardsPlayerValidator: MechanicValidatorProgressCardsPlayer | undefined,
                settlementPlayerState: MechanicSettlementPlayerState | undefined,
                cityPlayerState: MechanicCityPlayerState | undefined,
                cityWallPlayerState: MechanicCityWallPlayerState | undefined,
                cityImprovementPlayerState: MechanicCityImprovementsPlayerState | undefined,
                playerRoadState: MechanicRoadPlayerState | undefined,
                playerLongestRoadState: MechanicLongestRoadPlayerState | undefined,
                largestArmyPlayerState: MechanicLargestArmyPlayerState | undefined,
                specialBuildPhaseState: MechanicSpecialBuildPhaseState | undefined) {
        super(x, y, w, h, container)
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.player = player
        this.playerState = playerState
        this.developmentCardsPlayerState = developmentCardsPlayerState
        this.progressCardsPlayerValidator = progressCardsPlayerValidator
        this.settlementPlayerState = settlementPlayerState
        this.cityPlayerState = cityPlayerState
        this.cityWallPlayerState = cityWallPlayerState
        this.cityImprovementPlayerState = cityImprovementPlayerState
        this.roadPlayerState = playerRoadState
        this.longestRoadPlayerState = playerLongestRoadState
        this.largestArmyPlayerState = largestArmyPlayerState
        this.specialBuildPhaseState = specialBuildPhaseState

        if(myPlayer) {
            this.createMyPlayerNameAndImage()
            this.createMySpecialIconView()
        } else {
            this.createOpponentPlayerNameAndImage()
        }
        this.setPlayerAvatarClickAction()
        void this.updatePlayer()
        this.updateTurn()
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.GameStateUpdated: this.updateTurn(); break
            case UIGameEventEnums.PlayerControllerStateUpdated:
            case UIGameEventEnums.GameEnd: void this.updatePlayer(); break
            default: break
        }
    }

    protected abstract updateTurn(): void
    protected abstract createMyPlayerNameAndImage(): void
    protected abstract createOpponentPlayerNameAndImage(): void
    protected abstract createMySpecialIconView(): void
    abstract getContainerForAdditionalPlayerInformation(layerIndex: UIPlayerAdditionalInformationLayer): PIXI.Container

    protected getPlayerToHighlight(): PlayerColor {
        const lastPlayerToRollDice = this.specialBuildPhaseState?.lastPlayerToRollDice
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.SpecialBuildPhase && lastPlayerToRollDice != undefined) return lastPlayerToRollDice

        return this.uiGameManager.gameController.currentState.currentTurnPlayerColor
    }

    protected updateResourceCardTip(additionalTip?: TranslationObject) {
        const body: TranslationObject = {
            key: 'strings:game.playerInformation.resourceCard.body',
            options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext},
        }
        const bodyParagraphs: TranslationObject[] = [body]
        if(additionalTip) bodyParagraphs.push(additionalTip)
        const resourceCardTipTitle: TranslationObject = {key: 'strings:game.playerInformation.resourceCard.title'}
        this.resourceCardView.view.setTip(resourceCardTipTitle, bodyParagraphs)
    }

    private async updatePlayer() {
        this.updateTextView()

        // TODO: Refactor back to new Player State
        const playerState = this.uiGameManager.gameController.getPlayerState(this.player.color)
        const publicVictoryPoints = VictoryPointStateReader.totalPublicVictoryPoints(playerState.victoryPointsState)
        const privateVictoryPoints = VictoryPointStateReader.totalPrivateVictoryPoints(playerState.victoryPointsState)

        const doesPlayerHaveHiddenVictoryPoints = publicVictoryPoints != privateVictoryPoints && privateVictoryPoints != 0
        const victoryPointsText: TranslationObject = {
            key: 'strings:game.playerInformation.victoryPoint.display',
            options: {
                publicVictoryPoints: publicVictoryPoints,
                privateVictoryPoints: privateVictoryPoints,
                context: doesPlayerHaveHiddenVictoryPoints ? 'private' : '',
            },
        }
        this.victoryPointRibbon.updateText(victoryPointsText)

        const amountOfDevelopmentCards = this.developmentCardsPlayerState?.developmentCards.cards.length ?? this.progressCardsPlayerValidator?.progressCards.cards.length ?? 0
        this.developmentCardView.updateBadge(amountOfDevelopmentCards)

        const amountOfResourceCards = this.player.resourceCards.cards.length
        this.resourceCardView.updateBadge(amountOfResourceCards)
        const isPlayerAboveDiscardLimit = amountOfResourceCards > this.player.cardDiscardLimit
        const resourceCardTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.card_rescardback)
        const aboveDiscardLimitTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.card_rescardoverlimit)
        isPlayerAboveDiscardLimit ? this.resourceCardView.updateTexture(aboveDiscardLimitTexture) : this.resourceCardView.updateTexture(resourceCardTexture)
        const additionalDiscardLimitTip: TranslationObject = {
            key: 'strings:game.playerInformation.resourceCard.discardLimitTip',
            options: {count: this.player.cardDiscardLimit},
        }
        isPlayerAboveDiscardLimit ? this.updateResourceCardTip(additionalDiscardLimitTip) : this.updateResourceCardTip()

        void this.playerAvatar.refreshPlayerIcon()
    }

    protected abstract updateTextView()

    private setPlayerAvatarClickAction() {
        this.playerAvatar.setClickAction(() => {
            this.clickOnPlayerAction()
        })

        if(UIUtilsEjsVariables.isUserOnMobileApp) return
        this.playerName.setClickAction(() => {
            this.clickOnPlayerAction()
        })
    }

    private clickOnPlayerAction() {
        const isMyPlayerColor = this.player.color == this.uiGameManager.gameController.myColor
        if(isMyPlayerColor) {
            const isMyPlayerBotReplayPerspective = !this.player.isUser
            if(isMyPlayerBotReplayPerspective) return

            uiManagerEvents.openProfilePage(this.player.username, true)
            return
        }
        const canvas = this.uiGameManager.canvasController
        const playerInformationViewGlobalPosition = this.getGlobalPosition()
        const offsetX = UIUtilsEjsVariables.isUserOnMobileApp ? this.width / 6 : this.width / 3
        const popupX = (playerInformationViewGlobalPosition.x + offsetX) / canvas.appW
        const popupY = playerInformationViewGlobalPosition.y / canvas.appH
        const popupPosition = new PIXI.Point(popupX, popupY)
        const playerOptionsPopup = new UIPlayerOptionsPopup(this.uiGameManager, popupPosition, this.player)

        this.player.isUser ? playerOptionsPopup.buildInGamePlayerOptionsPopup() : playerOptionsPopup.buildInGameBotOptionsPopup()
    }

    destroy() {
        super.destroy()

        this.uiGameManager.eventController.unregisterUIGameMechanic(this)
    }
}
